// Maze-Rush decor (Level 6): three floating curved ribbons per section that
// twist and cross. Swing-log pendulums hang over each lane and sweep across
// it laterally — a hit knocks the player sideways and usually off the lane.
// The final pendulum (last section, last lane sample) is enormous.
//
// Layout positions come from Path.buildMazeRush.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import { laneWarp } from "./Path";

const TRACK_TOP_Y = 0.18;
const PLATFORM_COLOR = 0x9b5cf6;
const PLATFORM_RIM = 0xffd166;

// Per-section lane palette (5 sections, 3 lanes each).
const LANE_COLORS: number[][] = [
  [0xff5fa7, 0x6fc8ff, 0x6be09b],
  [0xffb86b, 0x8fe7e2, 0xd29bff],
  [0xffe066, 0xff8ad1, 0x6effd8],
  [0xff7a7a, 0x99c2ff, 0xffcf6e],
  [0x9bff7a, 0xffa0ff, 0x6fb8ff],
];

interface Pendulum {
  /** Anchor (pivot) world position. */
  pivotX: number;
  pivotY: number;
  s: number;
  /** Rope length (pivot → log centre when at rest). */
  rope: number;
  /** Log barrel radius + half-length. */
  radius: number;
  halfLength: number;
  /** Swing amplitude (radians) and angular frequency (rad/s). */
  amp: number;
  freq: number;
  phase: number;
  /** Current angle from vertical (positive = swung toward +X). */
  angle: number;
  pivotGroup: THREE.Group;
}

export interface MazeRushHandle {
  group: THREE.Group;
  supportMeshes: THREE.Object3D[];
  gatePositions: number[];
  tick: (clockMs: number) => void;
  /** Player-pendulum contact in world (s, lateral, worldY) coords. `worldY`
   *  must be the player's actual scene Y (i.e. including TRACK_TOP_Y + lane
   *  warp dy + jump), same frame as the pendulum's pivotY. */
  pendulumContact: (s: number, l: number, worldY: number, radius: number) => {
    normalL: number;
    normalY: number;
    penetration: number;
    barVelL: number;
  } | null;
}

function buildPlatform(width: number, depth: number): THREE.Group {
  const g = new THREE.Group();
  // Bumped from 0.36 → 0.6: ≥ 0.5m thickness rule.
  const thickness = 0.6;
  const top = new THREE.Mesh(
    new SoftBox(width, thickness, depth),
    new THREE.MeshStandardMaterial({ color: PLATFORM_COLOR, roughness: 0.55 }),
  );
  top.receiveShadow = true;
  top.castShadow = true;
  g.add(top);
  const rim = new THREE.Mesh(
    new SoftBox(width + 0.18, 0.14, depth + 0.18),
    new THREE.MeshStandardMaterial({ color: PLATFORM_RIM, roughness: 0.4 }),
  );
  rim.position.y = thickness / 2 + 0.04;
  g.add(rim);
  return g;
}

function buildLaneRibbon(
  section: { start: number; end: number },
  sectionIdx: number,
  laneIdx: number,
  halfWidth: number,
  color: number,
): THREE.Mesh {
  // Lane "ribbons" used to be flat 2-D strips with no thickness at all.
  // We now extrude them into 0.5m-tall slabs so the path looks like a
  // real walkway from the side — no more paper-thin floors.
  const SAMPLES = 64;
  const THICKNESS = 0.5;
  const positions: number[] = [];
  const indices: number[] = [];
  // Build TWO layers of strip vertices (top + bottom) so the slab has
  // visible side walls when viewed from anywhere except straight down.
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const s = section.start + (section.end - section.start) * t;
    const w = laneWarp(sectionIdx, laneIdx, t);
    const topY = w.dy + TRACK_TOP_Y;
    const botY = topY - THICKNESS;
    // 4 verts per ring: top-left, top-right, bottom-left, bottom-right.
    positions.push(w.dx - halfWidth, topY, s);
    positions.push(w.dx + halfWidth, topY, s);
    positions.push(w.dx - halfWidth, botY, s);
    positions.push(w.dx + halfWidth, botY, s);
    if (i < SAMPLES) {
      const v = i * 4;
      // Top face — what the player walks on.
      indices.push(v + 0, v + 1, v + 4);
      indices.push(v + 1, v + 5, v + 4);
      // Bottom face.
      indices.push(v + 2, v + 6, v + 3);
      indices.push(v + 3, v + 6, v + 7);
      // Left side wall.
      indices.push(v + 0, v + 4, v + 2);
      indices.push(v + 4, v + 6, v + 2);
      // Right side wall.
      indices.push(v + 1, v + 3, v + 5);
      indices.push(v + 3, v + 7, v + 5);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.55,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.castShadow = true;
  return mesh;
}

function buildLaneRibbonGlow(
  section: { start: number; end: number },
  sectionIdx: number,
  laneIdx: number,
  halfWidth: number,
  color: number,
): THREE.Mesh {
  const SAMPLES = 64;
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const s = section.start + (section.end - section.start) * t;
    const w = laneWarp(sectionIdx, laneIdx, t);
    positions.push(w.dx - halfWidth - 0.18, w.dy + TRACK_TOP_Y - 0.32, s);
    positions.push(w.dx + halfWidth + 0.18, w.dy + TRACK_TOP_Y - 0.32, s);
    if (i < SAMPLES) {
      const v = i * 2;
      indices.push(v, v + 1, v + 2);
      indices.push(v + 1, v + 3, v + 2);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.35,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  return new THREE.Mesh(geo, mat);
}

/** Build a Fall-Guys-style pendulum hanging from `pivot` with given size.
 *  Pivot is a Group rotated each frame around its local Z axis so the log
 *  swings laterally (perpendicular to the path's +Z direction). */
function buildPendulumGroup(rope: number, radius: number, halfLength: number, color: number): THREE.Group {
  const pivot = new THREE.Group();
  // Rope: a thin gold cylinder from pivot down to the log's top.
  const rope1 = new THREE.Mesh(
    new THREE.CylinderGeometry(0.07, 0.07, rope, 8),
    new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.4, metalness: 0.4 }),
  );
  rope1.position.y = -rope / 2;
  pivot.add(rope1);
  // The log itself: a long capsule, lying horizontally, dangling at the rope end.
  const log = new THREE.Mesh(
    new THREE.CapsuleGeometry(radius, halfLength * 2, 12, 18),
    new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.12,
      roughness: 0.5,
    }),
  );
  log.rotation.z = Math.PI / 2;
  log.position.y = -rope - radius * 0.1;
  log.castShadow = true;
  pivot.add(log);
  // Bright caps on each end of the log so it reads as a barrel.
  const capMat = new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.35, metalness: 0.25 });
  for (const x of [-halfLength, halfLength]) {
    const cap = new THREE.Mesh(new THREE.SphereGeometry(radius * 1.18, 18, 14), capMat);
    cap.position.set(x, -rope - radius * 0.1, 0);
    pivot.add(cap);
  }
  return pivot;
}

/** Tall yellow gantry frame straddling a pendulum, anchoring the rope.
 *  Visual only — gives the pendulums something to hang from. */
function buildGantry(pivotX: number, pivotY: number, s: number, span: number): THREE.Group {
  const g = new THREE.Group();
  const POST_H = pivotY + 1.4;
  const postMat = new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.45, metalness: 0.25 });
  for (const x of [pivotX - span, pivotX + span]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, POST_H, 12), postMat);
    post.position.set(x, POST_H / 2, s);
    g.add(post);
  }
  const beam = new THREE.Mesh(new SoftBox(span * 2 + 0.5, 0.34, 0.34), postMat);
  beam.position.set(pivotX, POST_H + 0.05, s);
  g.add(beam);
  return g;
}

export function buildMazeRushCourse(path: Path): MazeRushHandle {
  const mr = path.mazeRush;
  if (!mr) throw new Error("buildMazeRushCourse called on non-maze-rush path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const pendulums: Pendulum[] = [];

  // Platforms.
  const platformWidth = 10.4;
  for (const p of mr.platforms) {
    const len = p.end - p.start;
    const plat = buildPlatform(platformWidth, len);
    plat.position.set(0, 0, p.start + len / 2);
    group.add(plat);
    supportMeshes.push(...plat.children.filter((c) => c instanceof THREE.Mesh));
  }

  // Lane ribbons + pendulums.
  for (let secIdx = 0; secIdx < mr.sections.length; secIdx++) {
    const sec = mr.sections[secIdx]!;
    const palette = LANE_COLORS[secIdx % LANE_COLORS.length]!;
    const isFinalSection = secIdx === mr.sections.length - 1;

    for (let i = 0; i < mr.laneCount; i++) {
      const color = palette[i] ?? 0xffffff;
      group.add(buildLaneRibbonGlow(sec, secIdx, i, mr.laneHalfWidth, color));
      group.add(buildLaneRibbon(sec, secIdx, i, mr.laneHalfWidth, color));

      // Obstacle density by lane:
      //   • Lane 0 = SHORT + HARD path. 3 swing-log pendulums per section.
      //   • Lane 1 = MEDIUM path.       2 pendulums per section.
      //   • Lane 2 = LONG + EASY path.  1 pendulum per section.
      // The final section's lane 2 pendulum upsizes into a "finisher" hammer.
      const obstacleCount = i === 0 ? 3 : i === 1 ? 2 : 1;
      for (let k = 0; k < obstacleCount; k++) {
        // Spread pendulum positions across the section.
        const t = (k + 1) / (obstacleCount + 1);
        const isFinalPendulum = isFinalSection && i === mr.laneCount - 1 && k === 0;
        const warp = laneWarp(secIdx, i, t);
        const s = sec.start + (sec.end - sec.start) * t;
        const pivotX = warp.dx;
        const pivotY = warp.dy + TRACK_TOP_Y + (isFinalPendulum ? 6.0 : 3.4);
        const rope = isFinalPendulum ? 4.4 : 2.4;
        const radius = isFinalPendulum ? 1.1 : 0.42;
        const halfLength = isFinalPendulum ? 2.4 : 1.05;
        const span = halfLength + 0.6;

        group.add(buildGantry(pivotX, pivotY, s, span));
        const pivotGroup = buildPendulumGroup(rope, radius, halfLength, color);
        pivotGroup.position.set(pivotX, pivotY, s);
        group.add(pivotGroup);

        pendulums.push({
          pivotX,
          pivotY,
          s,
          rope,
          radius,
          halfLength,
          amp: isFinalPendulum ? 1.0 : 0.85,
          freq: isFinalPendulum ? 0.9 : 1.4 - i * 0.15 + k * 0.07,
          phase: (secIdx * 0.7 + i * 1.3 + k * 1.9) % (Math.PI * 2),
          angle: 0,
          pivotGroup,
        });
      }
    }
  }

  const tick = (clockMs: number) => {
    const tSec = clockMs / 1000;
    for (const p of pendulums) {
      p.angle = p.amp * Math.sin(tSec * p.freq + p.phase);
      p.pivotGroup.rotation.z = p.angle;
    }
  };

  /** Capsule-vs-sphere closest-distance test. The pendulum log is a capsule
   *  whose centre swings on the rope and whose long axis rotates with the
   *  swing (the rope is attached rigidly to the log's centre). The player is
   *  approximated as a sphere at chest height. We compute the closest point
   *  on the log's segment to the sphere centre and check whether the gap is
   *  less than (logRadius + sphereRadius). All three world axes participate
   *  — earlier versions ignored the path-direction (Z) axis, which made the
   *  hit window absurdly thin. */
  const pendulumContact = (
    s: number,
    l: number,
    worldY: number,
    radius: number,
  ) => {
    // Player as a sphere at chest height (~0.55 m above feet) with a small
    // forgiveness pad so glancing brushes still register.
    const PLAYER_CHEST_OFFSET = 0.55;
    const PLAYER_PAD = 0.32;
    const pX = l;
    const pY = worldY + PLAYER_CHEST_OFFSET;
    const pZ = s;
    const effPlayerR = radius + PLAYER_PAD;
    for (const p of pendulums) {
      const sinA = Math.sin(p.angle);
      const cosA = Math.cos(p.angle);
      // Log centre in world: pivot + rope * (sinA, -cosA, 0).
      const Cx = p.pivotX + sinA * p.rope;
      const Cy = p.pivotY - cosA * p.rope;
      const Cz = p.s;
      // Quick reject: log centre is in the plane z=p.s, axis lies in XY,
      // so the only z extent comes from the log radius. If the player is
      // further than (logRadius + effPlayer) along z, skip.
      const dz = pZ - Cz;
      const sumR = p.radius + effPlayerR;
      if (Math.abs(dz) > sumR) continue;
      // Log axis direction in world after rotating around Z by p.angle.
      // The log was authored along local +X, so after rotation its axis is
      // (cos(angle), sin(angle), 0).
      const Ax = cosA;
      const Ay = sinA;
      // Closest point on the log's segment to the player sphere centre.
      const rx = pX - Cx;
      const ry = pY - Cy;
      const rz = dz;
      let t = rx * Ax + ry * Ay; // (Az = 0, so rz drops out of projection)
      if (t > p.halfLength) t = p.halfLength;
      else if (t < -p.halfLength) t = -p.halfLength;
      const closestX = Cx + Ax * t;
      const closestY = Cy + Ay * t;
      const closestZ = Cz;
      const ex = pX - closestX;
      const ey = pY - closestY;
      const ez = pZ - closestZ;
      const dist2 = ex * ex + ey * ey + ez * ez;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      // Normal points from closest segment point → player. Project into the
      // (lateral, vertical) plane for the Game's knockback math.
      const nl = ex / dist;
      const ny = ey / dist;
      // Instantaneous lateral speed of the log centre + the contact's
      // offset along the axis. dθ/dt is bounded by amp*freq; tangential
      // velocity at the log centre = rope * dθ/dt. Sign mirrors normal so
      // the impulse always pushes the player away.
      const barVelL = Math.abs(p.amp * p.freq * p.rope) * Math.sign(nl || 1);
      return { normalL: nl, normalY: ny, penetration: sumR - dist, barVelL };
    }
    return null;
  };

  return { group, supportMeshes, gatePositions: mr.gates, tick, pendulumContact };
}
